/**
 * publicAuditService.js — el auditor SEO de la landing.
 *
 * Endpoints — ver `QR/api/public_audit_views.py`:
 *   POST /public/audit/search   {query, near?, language}  → {sid, results[≤4]}
 *   POST /public/audit/report   {sid, placeId}            → {report}
 *   GET  /public/audit/status                             → {enabled}
 *
 * El `sid` no es decorativo: `/report` solo acepta un `placeId` que `/search`
 * acabó de ofrecer en esa misma sesión. Hay que devolverlo tal cual llegó, sin
 * inventárselo ni reutilizar uno viejo.
 *
 * Va por `httpClient` como todo lo demás. Es anónimo, así que no hay 401 que
 * temer; el interceptor no estorba.
 */

import httpClient from './httpClient';

const BASE = '/public/audit';

/**
 * Los códigos que puede devolver el backend, para que la interfaz pueda
 * distinguir «no hemos podido» de «no existe». Cualquier otro se trata como
 * fallo genérico.
 */
export const AUDIT_ERRORS = {
  INVALID_QUERY: 'invalid_query',
  NOT_FOUND: 'not_found',
  BUDGET: 'budget_exhausted',
  UNAVAILABLE: 'provider_unavailable',
  TEMPORARY: 'temporarily_unavailable',
  SESSION_EXPIRED: 'session_expired',
  SESSION_EXHAUSTED: 'session_exhausted',
  PLACE_NOT_IN_SESSION: 'place_not_in_session',
  RATE_LIMITED: 'rate_limited',
  DISABLED: 'disabled',
};

/** Traduce lo que sea que haya fallado a uno de los códigos de arriba. */
export function auditErrorCode(error) {
  const status = error?.response?.status;
  if (status === 429) return AUDIT_ERRORS.RATE_LIMITED;
  const code = error?.response?.data?.code;
  // Un 404 con `code: 'not_found'` es ambiguo: o el interruptor está apagado o
  // Google no conoce ese local. Para el visitante da igual —no hay informe—,
  // pero conviene no prometerle que puede reintentar.
  if (status === 404) return code === 'not_found' ? AUDIT_ERRORS.NOT_FOUND : AUDIT_ERRORS.DISABLED;
  return code || AUDIT_ERRORS.UNAVAILABLE;
}

/**
 * ¿Está encendido el auditor?
 *
 * Lo pregunta el componente antes de pintarse. Con el interruptor apagado la
 * sección no se monta, en vez de enseñar una caja de búsqueda que devuelve
 * errores — que es lo que parece roto.
 *
 * Ante cualquier fallo devuelve `false`: si no podemos confirmar que está
 * encendido, no se enseña. Es la dirección segura para algo que cuesta dinero.
 */
export async function getAuditStatus() {
  try {
    const { data } = await httpClient.get(`${BASE}/status`);
    return !!data?.enabled;
  } catch {
    return false;
  }
}

/**
 * Busca hasta 4 negocios. `near` (ciudad o barrio) acota.
 * Devuelve `{sid, results: [{placeId, mainText, secondaryText}]}`.
 */
export async function searchPlaces({ query, near, language }) {
  const { data } = await httpClient.post(`${BASE}/search`, { query, near, language });
  return { sid: data?.sid || null, results: data?.results || [] };
}

/** El análisis del local elegido. Devuelve el `report` del backend. */
export async function getAuditReport({ sid, placeId }) {
  const { data } = await httpClient.post(`${BASE}/report`, { sid, placeId });
  return data?.report || null;
}
